import type { AxiosError, AxiosRequestConfig } from 'axios'
import axios from 'axios'
import qs from 'qs'

import envConfig from '@acre/config'

import { LutherException } from '../resolvers/util'

interface RequestMeta {
  meta?: { start?: number }
}

type AxiosRequestConfigMeta = AxiosRequestConfig & RequestMeta

interface AxiosResponseMeta<T = any> {
  data: T
  status: number
  statusText: string
  config: AxiosRequestConfigMeta
  request?: any
}

interface AxiosResponseDuration extends AxiosResponseMeta {
  duration?: number
}

const defaultParams: AxiosRequestConfig = {
  withCredentials: true,
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
}

export interface CreateAxiosInstanceConfig {
  useApiKey?: boolean
  useLutherInterceptor?: boolean
  useCpApiKey?: boolean
}

const createAxiosInstance = (axiosConfig: AxiosRequestConfig, config?: CreateAxiosInstanceConfig) => {
  const instance = axios.create({ ...defaultParams, ...axiosConfig })

  if (config?.useCpApiKey) {
    instance.defaults.headers.common['X-Api-Key'] = envConfig.CLIENT_PORTAL_API_KEY
  }

  if (config?.useApiKey) {
    instance.defaults.headers.common['X-Api-Key'] = envConfig.API_KEY
  }

  instance.interceptors.request.use((config) => {
    const requestConfig = config as AxiosRequestConfigMeta
    requestConfig.meta = { start: Date.now() }

    return config
  })

  instance.interceptors.response.use(
    (response) => {
      const res = response as AxiosResponseDuration

      if (res.config && res.config.meta && res.config.meta.start) {
        res.duration = Date.now() - res.config.meta.start
      }

      return response
    },
    (err: AxiosError<{ exception: Exception; message: string }>) => {
      if (err.response) {
        const errorResponse = err.response.data
        const url = err.response.config.url!

        if (errorResponse.exception) {
          if (config?.useLutherInterceptor) {
            throw new LutherException(err?.message, err.response?.status, url, errorResponse.exception)
          }

          err.message = errorResponse.exception.description
        }

        if (errorResponse.message) {
          err.message = errorResponse.message
        }
      }

      throw err
    },
  )

  return instance
}

export default createAxiosInstance
